/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

// Types
export interface DropdownOption {
  icon?: any;
  text: string;
  badge?: string | null;
  [key: string]: any;
}

export interface CategoryItem {
  id: string;
  name?: string;
  options: DropdownOption[];
  [key: string]: any;
}

export interface CategoryDropdownProps {
  buttonText?: string;
  categories?: CategoryItem[] | DropdownOption[];
  buttonClassName?: string;
  dropdownClassName?: string;
  categoryClassName?: string;
  optionClassName?: string;
  hoveredCategoryBg?: string;
  iconColor?: string;
  fontFamily?: string;
  onOptionClick?: (option: DropdownOption, categoryId?: string | null) => void;
}

export default function CategoryDropdown({ 
  buttonText = 'Dropdown',
  categories = [],
  buttonClassName = '',
  dropdownClassName = '',
  categoryClassName = '',
  optionClassName = '',
  hoveredCategoryBg = 'bg-[#4949DE33]',
  iconColor = 'text-[#4949DE]',
  fontFamily = 'Instrument Sans, sans-serif',
  onOptionClick = () => {},
}: CategoryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if categories have names (hierarchical) or just options (flat)
  const hasCategories = categories.length > 0 && (categories[0] as CategoryItem)?.name;
  const displayCategories = hasCategories ? (categories as CategoryItem[]) : null;
  const flatOptions = !hasCategories ? (categories as DropdownOption[]) : null;

  // Set initial hovered category if hierarchical
  useEffect(() => {
    if (hasCategories && categories.length > 0) {
      setHoveredCategory(categories[0].id);
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (option: DropdownOption, categoryId: string | null = null) => {
    onOptionClick(option, categoryId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Gabarito:wght@500&family=Instrument+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap');`}
      </style>

      {/* Dropdown Button */}
      <button 
        type="button" 
        className={`flex items-center gap-1 text-gray-800 ${buttonClassName}`}
        onClick={() => setIsOpen(isOpen => !isOpen)}
      >
        {buttonText} 
        <ChevronDown 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute top-full mt-2 left-0 flex gap-0 z-50 ${dropdownClassName}`}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          style={{ fontFamily }}
        >
          {/* Hierarchical Layout: Categories + Options */}
          {displayCategories ? (
            <>
              {/* Categories List */}
              <div className="bg-[white] rounded-l-xl shadow-2xl border border-gray-200 w-56 overflow-hidden">
                {displayCategories.map((category) => (
                  <div
                    key={category.id}
                    onMouseEnter={() => setHoveredCategory(category.id)}
                    onClick={() => handleOptionClick(category as any)}
                    className={`px-6 py-4 cursor-pointer transition-colors ${categoryClassName} ${
                      hoveredCategory === category.id
                        ? `${hoveredCategoryBg} text-gray-900`
                        : 'text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-xs font-semibold">{category.name}</div>
                  </div>
                ))}
              </div>

              {/* Options Panel */}
              {hoveredCategory && (
                <div className="bg-[white] rounded-r-xl shadow-2xl border border-l-0 border-gray-200 w-80 p-4 space-y-2">
                  {displayCategories
                    .find((cat) => cat.id === hoveredCategory)
                    ?.options.map((option, idx) => {
                      const IconComponent = option.icon;
                      return (
                        <div
                          key={idx}
                          onClick={() => handleOptionClick(option, hoveredCategory)}
                          className={`flex items-center gap-3 rounded-lg cursor-pointer hover:bg-gray-50 p-2 transition-colors ${optionClassName}`}
                        >
                          {IconComponent && (
                            <div className="rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className={`w-4 h-4 ${iconColor}`} />
                            </div>
                          )}
                          <span className="text-gray-700 text-xs font-medium flex-1">
                            {option.text}
                          </span>
                          {option.badge && (
                            <span className={`text-xs px-2 rounded font-light ${
                              option.badge === 'New'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-[#FFEDD7] text-[#F89522]'
                            }`}>
                              {option.badge}
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          ) : (
            /* Flat Layout: Options Only */
            <div className="bg-[white] rounded-xl shadow-2xl border border-gray-200 w-80 p-4 space-y-2">
              {flatOptions?.map((option, idx) => {
                const IconComponent = option.icon;
                return (
                  <div
                    key={idx}
                    onClick={() => handleOptionClick(option)}
                    className={`flex items-center gap-3 rounded-lg cursor-pointer hover:bg-gray-50 p-2 transition-colors ${optionClassName}`}
                  >
                    {IconComponent && (
                      <div className="rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className={`w-4 h-4 ${iconColor}`} />
                      </div>
                    )}
                    <span className="text-gray-700 text-xs font-medium flex-1">
                      {option.text}
                    </span>
                    {option.badge && (
                      <span className={`text-xs px-2 rounded font-light ${
                        option.badge === 'New'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-[#FFEDD7] text-[#F89522]'
                      }`}>
                        {option.badge}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}